import React, { useState, useCallback, useMemo, useEffect } from "react";

import { makeStyles } from "tss-react/mui";

import DialogActions from "@mui/material/DialogActions";
import Grid from "@mui/material/Grid";
import Box from "@mui/material/Box";
import AppBar from "@mui/material/AppBar";
import Toolbar from "@mui/material/Toolbar";
import Backdrop from "@mui/material/Backdrop";
import CircularProgress from "@mui/material/CircularProgress";
import IconButton from "@mui/material/IconButton";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import Paper from "@mui/material/Paper";
import Table from "@mui/material/Table";
import Divider from "@mui/material/Divider";
import TableSortLabel from "@mui/material/TableSortLabel";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";

import { visuallyHidden } from "@mui/utils";

import CloseIcon from "@mui/icons-material/Close";
import SearchIcon from "@mui/icons-material/Search";
import ClearIcon from "@mui/icons-material/Clear";

import { ButtonUI, KMKDialog } from "../../../../Common";
import { setPrevState } from "../../../../../util/common";
import { DATA_SOURCE } from "../../../../../util/Data/DataModeling";

const useStyles = makeStyles()((theme) => ({
  dialogPaper: {
    "& .MuiPaper-root": {
      width: "100%",
    },
    "& .MuiFormHelperText-root": {
      marginLeft: 0,
    },
    "& .MuiGrid-item": {
      paddingTop: "15px",
    },
    "& .MuiGrid-item .MuiTextField-root": {
      marginTop: 0,
    },
    "& .MuiDivider-root": {
      marginTop: "5px",
      marginBottom: "9px",
    },
    "& .MuiFormControl-root": {
      margin: 0,
    },
    "& .MuiSvgIcon-root": {
      verticalAlign: "middle",
    },
  },
  divider: {
    marginTop: "5px",
    marginBottom: "9px",
  },
  container: {
    padding: "24px 24px 0 24px",
    backgroundColor: "#FFFFFF",
  },
  bodyContainer: {
    width: "100%",
    marginLeft: "0",
    marginBottom: "0",
  },
  Toolbar: {
    fontSize: "1.1rem",
    backgroundColor: "white",
    color: "black",
  },
  searchContainer: {
    minWidth: "250px",
  },
  tableContainer: {
    height: "312px",
    overflow: "auto",
    "& .MuiTableCell-root": {
      wordBreak: "break-all",
    },
    "& .MuiTableCell-head": {
      fontWeight: "600 !important",
    },
  },
  TableHead: {
    "& th": {
      position: "sticky",
      top: "0px",
      zIndex: 2,
      backgroundColor: "rgb(255, 255, 255)",
      fontWeight: "600 !important",
    },
  },
  selectableRow: {
    cursor: "pointer",
  },
  selectedRow: {
    background: "rgba(0, 0, 0, 0.1)",
    "&:hover": {
      background: "rgba(0, 0, 0, 0.1)",
    },
  },
  disabledRow: {
    cursor: "no-drop",
    "& .MuiTableCell-root": {
      color: "rgba(0, 0, 0, 0.6) !important",
    },
    "&:hover": {
      "& .MuiTableCell-root": {
        color: "rgba(0, 0, 0, 0.6) !important",
      },
    },
  },
}));

//Headcells
const headCells = [
  {
    id: "displayName",
    numeric: false,
    disablePadding: false,
    label: "Display Name",
    sortable: true,
  },
  {
    id: "name",
    numeric: false,
    disablePadding: false,
    label: "System Name",
    sortable: true,
  },
  {
    id: "fieldCount",
    numeric: true,
    disablePadding: false,
    label: "Field Count",
    sortable: true,
  },
];

//Table Head with sorting
function TableHeadWithSorting(props) {
  const { order, orderBy, onRequestSort } = props;
  const createSortHandler = (property) => (event) => {
    onRequestSort(event, property);
  };

  return (
    <TableHead>
      <TableRow>
        {headCells.map((headCell, i) => (
          <TableCell
            style={i !== 2 ? { width: "250px" } : { width: "100px" }}
            key={headCell.id}
            align={headCell.numeric ? "right" : "left"}
            padding={headCell.disablePadding ? "none" : "normal"}
            sortDirection={orderBy === headCell.id ? order : false}
          >
            {headCell.sortable ? (
              <TableSortLabel
                active={orderBy === headCell.id}
                direction={orderBy === headCell.id ? order : "asc"}
                onClick={createSortHandler(headCell.id)}
              >
                {headCell.label}
                {orderBy === headCell.id ? (
                  <Box component="span" sx={visuallyHidden}>
                    {order === "desc" ? "sorted descending" : "sorted ascending"}
                  </Box>
                ) : null}
              </TableSortLabel>
            ) : (
              headCell.label
            )}
          </TableCell>
        ))}
      </TableRow>
    </TableHead>
  );
}

export default React.memo(function DSPopup({ setOpen, title, sources, handleLoadClick, fetchDSRequiredInfo }) {
  const { classes } = useStyles();

  const [browserState, setBrowserState] = useState({
    loading: false,
    search: "",
    order: "asc",
    orderBy: "displayName",
    selected: "",
  });

  //Close popup
  const handleCancel = useCallback(() => {
    setOpen(false);
  }, [setOpen]);

  //Comparing for sorting
  function descendingComparator(a, b, orderBy) {
    if (orderBy === "fieldCount") {
      if (b[orderBy] < a[orderBy]) {
        return -1;
      }
      if (b[orderBy] > a[orderBy]) {
        return 1;
      }
      return 0;
    } else {
      if (b[orderBy]?.toLowerCase() < a[orderBy]?.toLowerCase()) {
        return -1;
      } else if (b[orderBy]?.toLowerCase() > a[orderBy]?.toLowerCase()) {
        return 1;
      }
      return 0;
    }
  }

  //Comparing order
  const getComparator = useCallback((order, orderBy) => {
    return order === "desc"
      ? (a, b) => descendingComparator(a, b, orderBy)
      : (a, b) => -descendingComparator(a, b, orderBy);
  }, []);

  // This method is created for cross-browser compatibility, if you don't
  // need to support IE11, you can use Array.prototype.sort() directly
  function stableSort(array, comparator) {
    const stabilizedThis = array.map((el, index) => [el, index]);
    stabilizedThis.sort((a, b) => {
      const order = comparator(a[0], b[0]);
      if (order !== 0) {
        return order;
      }
      return a[1] - b[1];
    });
    return stabilizedThis.map((el) => el[0]);
  }

  //Sort cloumn click
  const handleRequestSort = (event, property) => {
    const isAsc = browserState.orderBy === property && browserState.order === "asc";
    setPrevState(setBrowserState, { order: isAsc ? "desc" : "asc", orderBy: property });
  };

  const handleClearSearch = () => {
    setPrevState(setBrowserState, { search: "" });
  };

  // If this is the first time data source information required then fetch from backend
  useEffect(() => {
    if (!sources) fetchDSRequiredInfo();
  }, [fetchDSRequiredInfo, sources]);

  // Filtered data for changes done as column ordering change and serch value
  const $filteredData = useMemo(() => {
    let filteredData = sources
      ? sources.filter(
          ({ displayName, name }) =>
            displayName?.toLowerCase().includes(browserState.search.toLowerCase()) ||
            name.toLowerCase().includes(browserState.search.toLowerCase())
        )
      : [];

    return stableSort(filteredData, getComparator(browserState.order, browserState.orderBy));
  }, [sources, getComparator, browserState.order, browserState.orderBy, browserState.search]);

  return (
    <>
      <Backdrop
        sx={{ backgroundColor: "transparent", zIndex: (theme) => theme.zIndex.drawer + 1 }}
        open={browserState.loading}
      >
        <CircularProgress color="primary" />
      </Backdrop>
      <KMKDialog className={classes.dialogPaper} maxWidth="md" open={true} onClose={handleCancel}>
        <AppBar sx={{ position: "relative" }}>
          <Toolbar className={classes.Toolbar} variant="dense">
            {title}
          </Toolbar>
          <IconButton
            aria-label="close"
            onClick={handleCancel}
            sx={{
              position: "absolute",
              right: 8,
              top: 4,
              color: "black",
              mr: "10px",
            }}
          >
            <CloseIcon />
          </IconButton>
        </AppBar>
        <Box sx={{ width: "100%" }} className={classes.container}>
          <Backdrop
            sx={{ backgroundColor: "transparent", zIndex: (theme) => theme.zIndex.drawer + 1 }}
            open={browserState.loading}
          >
            <CircularProgress color="primary" />
          </Backdrop>
          <Grid container spacing={0} sx={{ mb: 2 }} className={classes.bodyContainer}>
            <Grid
              sx={{ py: 2 }}
              justifyContent={"flex-start"}
              size={{
                xs: 6,
                md: 6
              }}></Grid>
            <Grid
              sx={{ py: 2 }}
              justifyContent={"flex-end"}
              display={"flex"}
              size={{
                xs: 6,
                md: 6
              }}>
              <Box className={classes.searchContainer}>
                <TextField
                  margin="normal"
                  value={browserState.search}
                  onChange={(e) => setPrevState(setBrowserState, { search: e.target.value })}
                  placeholder="Search"
                  variant="standard"
                  InputProps={{
                    startAdornment: <SearchIcon color="primary" />,
                    ...(browserState.search && {
                      endAdornment: (
                        <IconButton size="small" onClick={handleClearSearch}>
                          <ClearIcon color="primary" />
                        </IconButton>
                      ),
                    }),
                  }}
                  fullWidth
                />
              </Box>
            </Grid>
            <Divider className={classes.divider} />
            <TableContainer component={Paper} elevation={3} className={classes.tableContainer}>
              <Table stickyHeader sx={{ minWidth: 400 }} size="small">
                <TableHeadWithSorting
                  className={classes.TableHead}
                  order={browserState.order}
                  orderBy={browserState.orderBy}
                  onRequestSort={handleRequestSort}
                />
                {$filteredData.length > 0 ? (
                  <TableBody>
                    {$filteredData.map(({ id, displayName, name, fieldCount }) => {
                      return (
                        <TableRow
                          key={id}
                          sx={{
                            "&:last-child td, &:last-child th": { border: 0 },
                          }}
                          // Add class(es) based on File Selection condition
                          className={
                            fieldCount === 0
                              ? `${classes.disabledRow}`
                              : `${`${classes.selectableRow} ${
                                  name === browserState.selected ? classes.selectedRow : ""
                                }`}`
                          }
                          onClick={() => {
                            if (fieldCount !== 0) {
                              // Set selected variable with filename selected
                              setPrevState(setBrowserState, { selected: browserState.selected ? false : name });
                            }
                          }}
                          hover
                        >
                          <TableCell align="left">{displayName}</TableCell>
                          <TableCell align="left">{name}</TableCell>
                          <TableCell align="right">{fieldCount}</TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                ) : (
                  <TableBody></TableBody>
                )}
              </Table>
              {!$filteredData.length && (
                <div
                  style={{
                    height: "85%",
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                  }}
                >
                  {`No ${DATA_SOURCE.singular} Found`}
                </div>
              )}
            </TableContainer>
            <Grid justifyContent={"flex-start"} size={12}>
              <Typography sx={{ color: (theme) => theme.palette.error.main }}>
                {`*Any field in ${DATA_SOURCE.singular.toLowerCase()} that has deletion field configured will not be loaded.`}
              </Typography>
              <Typography sx={{ color: (theme) => theme.palette.error.main }}>
                {`NOTE: Loading fields from the ${DATA_SOURCE.singular.toLowerCase()} will remove all the matching configuration rules.`}
              </Typography>
            </Grid>
          </Grid>
          <DialogActions sx={{ padding: "20px 0 16px 0" }}>
            <ButtonUI variant="contained" color="secondary" onClick={handleCancel}>
              Cancel
            </ButtonUI>
            <ButtonUI
              onClick={() => {
                handleLoadClick(browserState.selected);
              }}
              variant="contained"
              color="success"
              style={{ marginLeft: 15 }}
              disabled={browserState.loading || !browserState.selected}
            >
              Load
            </ButtonUI>
          </DialogActions>
        </Box>
      </KMKDialog>
    </>
  );
});
